import {
  Button,
  Checkbox,
  Form,
  InputNumber,
  Modal,
  Select,
  Space,
  Typography,
} from 'antd'
import { useState } from 'react'
import { useCasesListQuery } from '../Cases/casesAdminQueries'
import PresetEditorModal from '../Presets/PresetEditorModal'
import { PresetForm } from '../Presets/presetsAdminModel'
import { useTagsListQuery } from '../Tags/tagsAdminQueries'
import {
  buildRunCreateInput,
  emptyRunForm,
  isSelectionEmpty,
  RunSelectionForm,
} from './runsAdminModel'
import { useCreateRunMutation } from './runsAdminQueries'

type Props = {
  open: boolean
  onClose: () => void
}

// The tag picker is admin-scale; load the first page at the backend's cap.
const TAG_FILTERS = { page: 1, pageSize: 50 }

const CreateRunModal = ({ open, onClose }: Props) => {
  const [form, setForm] = useState<RunSelectionForm>(emptyRunForm)
  const set = <K extends keyof RunSelectionForm>(
    key: K,
    value: RunSelectionForm[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }))

  const [savingPreset, setSavingPreset] = useState(false)
  const createMutation = useCreateRunMutation()
  const { data: casesData } = useCasesListQuery()
  const { data: tagData } = useTagsListQuery(TAG_FILTERS)

  const caseOptions =
    casesData?.cases.map((c) => ({ label: c.name, value: c.case_id })) ?? []
  const tagOptions =
    tagData?.tags.map((t) => ({ label: t.name, value: t.tag_id })) ?? []

  const selectionEmpty = isSelectionEmpty(form)

  const handleClose = () => {
    setForm(emptyRunForm())
    onClose()
  }

  const handleOk = () => {
    if (selectionEmpty) {
      return
    }
    createMutation.mutate(buildRunCreateInput(form), { onSuccess: handleClose })
  }

  const presetSeed: Partial<PresetForm> = {
    caseIds: form.caseIds,
    tagIds: form.tagIds,
    includeMutants: form.includeMutants,
    trials: form.trials,
  }

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      onOk={handleOk}
      okText="Start run"
      okButtonProps={{ disabled: selectionEmpty }}
      title="New Run"
      confirmLoading={createMutation.isLoading}
      destroyOnClose
      footer={[
        <Button
          key="save-preset"
          // Persist the current selection as a reusable preset (#108). Same
          // selection requirement as starting a run.
          disabled={selectionEmpty}
          onClick={() => setSavingPreset(true)}
        >
          Save as preset
        </Button>,
        <Button key="cancel" onClick={handleClose}>
          Cancel
        </Button>,
        <Button
          key="start"
          type="primary"
          disabled={selectionEmpty}
          loading={createMutation.isLoading}
          onClick={handleOk}
        >
          Start run
        </Button>,
      ]}
    >
      {savingPreset && (
        <PresetEditorModal
          open
          initialForm={presetSeed}
          onClose={() => setSavingPreset(false)}
        />
      )}
      <Form layout="vertical">
        <Form.Item
          label="Cases"
          // Cases and tags combine as a union — the run covers every case
          // selected directly plus every case carrying a selected tag.
          extra="Pick cases to run directly."
          style={{ marginBottom: 12 }}
        >
          <Select
            mode="multiple"
            allowClear
            value={form.caseIds}
            onChange={(value) => set('caseIds', value)}
            options={caseOptions}
            placeholder="Select cases"
            optionFilterProp="label"
            notFoundContent="No cases — create some under Cases first."
          />
        </Form.Item>
        <Form.Item
          label="Tags"
          extra="Every case carrying a selected tag is included too."
          style={{ marginBottom: 12 }}
        >
          <Select
            mode="multiple"
            allowClear
            value={form.tagIds}
            onChange={(value) => set('tagIds', value)}
            options={tagOptions}
            placeholder="Select tags"
            optionFilterProp="label"
            notFoundContent="No tags — create some under Tags first."
          />
        </Form.Item>
        <Space size="large" align="center" style={{ marginBottom: 8 }}>
          <Checkbox
            checked={form.includeMutants}
            onChange={(e) => set('includeMutants', e.target.checked)}
          >
            Include mutants
          </Checkbox>
          <span>
            Trials per case{' '}
            <InputNumber
              min={1}
              value={form.trials}
              onChange={(value) => set('trials', value ?? 1)}
            />
          </span>
        </Space>
        <Typography.Text type="secondary" style={{ display: 'block' }}>
          Model and inference settings are chosen automatically.
        </Typography.Text>
        {selectionEmpty && (
          <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            Select at least one case or tag to start a run.
          </Typography.Text>
        )}
      </Form>
    </Modal>
  )
}

export default CreateRunModal
